import { useState } from 'react';
import { Button, Loading } from '../../../components';

import { Modal as ModalAnt } from 'antd';

import { BaseRecord } from '../../../interfaces/BaseRecord';
import ModalHeader from '../../../components/generic/ModalHeader';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_SUPPLIER_MATERIAL,
  DELETE_SUPPLIER_MATERIAL,
  EDIT_SUPPLIER_MATERIAL,
  GET_SUPPLIER_MATERIALS,
} from '../queries/suppliers';
import DeleteMaterial from './modals/DeleteMaterial';
import CreateOrEditMaterial from './modals/CreateOrEditMaterial';
import MaterialItem from './MaterialItem';
import { ActiveMaterialType } from './types';
import FetchError from '../../../components/generic/FetchError';

type MaterialsFormProps = {
  isMaterialsModalOpen: boolean;
  recordToEdit: BaseRecord | null;
  onCancel: () => void;
};

const MaterialsForm = ({
  isMaterialsModalOpen,
  recordToEdit,
  onCancel,
}: MaterialsFormProps) => {
  const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
  const [isDeleteMaterialModalOpen, setIsDeleteMaterialModalOpen] =
    useState(false);
  const [activeMaterial, setActiveMaterial] =
    useState<ActiveMaterialType | null>(null);

  const {
    data: supplierMaterials,
    loading,
    error,
  } = useQuery<{ findAllMaterialsBySupplier: ActiveMaterialType[] }>(
    GET_SUPPLIER_MATERIALS,
    {
      variables: { uuid: recordToEdit?.uuid },
    },
  );

  const mutation = activeMaterial
    ? EDIT_SUPPLIER_MATERIAL
    : CREATE_SUPPLIER_MATERIAL;

  const [material, { loading: materialLoading, error: materialError }] =
    useMutation(mutation, {
      refetchQueries: ['GetSupplierMaterials'],
    });

  const [
    materialDelete,
    { loading: deleteMaterialLoading, error: deleteMaterialError },
  ] = useMutation(DELETE_SUPPLIER_MATERIAL, {
    refetchQueries: ['GetSupplierMaterials'],
  });

  const handleShowAddMaterialModal = () => {
    if (activeMaterial) {
      setActiveMaterial(null);
    }
    setIsMaterialModalOpen(true);
  };

  const handleShowEditMaterialModal = (material: ActiveMaterialType) => {
    setActiveMaterial(material);
    setIsMaterialModalOpen(true);
  };

  const handleCloseEditMaterialModal = () => {
    if (activeMaterial) {
      setActiveMaterial(null);
    }
    setIsMaterialModalOpen(false);
  };

  const handleShowDeleteMaterialModal = (material: ActiveMaterialType) => {
    setActiveMaterial(material);
    setIsDeleteMaterialModalOpen(true);
  };

  const handleCloseMaterialDeleteModal = () => {
    setIsDeleteMaterialModalOpen(false);
    setActiveMaterial(null);
  };

  const handleEditMaterial = async ({
    material: materialName,
  }: {
    material: string;
  }) => {
    if (!materialName || materialName === activeMaterial?.name) return;

    let response;

    try {
      if (activeMaterial) {
        response = await material({
          variables: {
            data: { name: materialName, uuid: activeMaterial?.uuid },
          },
        });
      } else {
        response = await material({
          variables: {
            data: {
              name: materialName,
              supplier: {
                uuid: recordToEdit?.uuid,
              },
            },
          },
        });
      }
      if (response.data) handleCloseEditMaterialModal();
    } catch (e) {
      console.log('Mutation error', e);
    }
  };

  const handleDeleteMaterial = async () => {
    try {
      const response = await materialDelete({
        variables: {
          uuid: activeMaterial?.uuid,
        },
      });

      if (response.data) handleCloseMaterialDeleteModal();
    } catch (e) {
      console.log('Mutation error', e);
    }
  };

  return (
    <>
      <ModalAnt
        title={<ModalHeader title="Add/Edit Materials" />}
        open={isMaterialsModalOpen}
        onCancel={onCancel}
        footer={null}
        className="custom-modal"
        width={650}
        centered
        bodyStyle={{
          maxHeight: 480,
          scrollBehavior: 'smooth',
          overflowY: 'auto',
          resize: 'vertical',
        }}
      >
        {!loading ? (
          <div className="max-w-130 mx-auto">
            <div className="bg-[#F8F8F8] w-full border rounded-[10px]">
              <div className="h-12 flex items-center text-primary-100 text-[16px] font-medium uppercase py-4 px-5">
                {recordToEdit?.supplierName}
              </div>
              {supplierMaterials &&
              supplierMaterials.findAllMaterialsBySupplier.length > 0 ? (
                supplierMaterials.findAllMaterialsBySupplier.map((material) => (
                  <MaterialItem
                    key={material.name}
                    materialName={material.name}
                    onEdit={() => handleShowEditMaterialModal(material)}
                    onDelete={() => handleShowDeleteMaterialModal(material)}
                  />
                ))
              ) : (
                <div className="w-full bg-white border-t border-[#CDCDCD] border-collapse flex items-center justify-between py-1 pl-8 pr-4">
                  No materials found...
                </div>
              )}
            </div>
            {error && <FetchError error={error.message} />}
            <div>
              <div className="w-[165px] mx-auto">
                <Button
                  type="add"
                  className="mt-8 py-1 text-sm font-black"
                  onClick={handleShowAddMaterialModal}
                >
                  + Add
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <Loading />
        )}
      </ModalAnt>
      <CreateOrEditMaterial
        activeMaterial={activeMaterial}
        isMaterialModalOpen={isMaterialModalOpen}
        handleCloseEditMaterialModal={handleCloseEditMaterialModal}
        onFinish={handleEditMaterial}
        isLoading={materialLoading}
        error={materialError}
      />
      <DeleteMaterial
        isDeleteMaterialModalOpen={isDeleteMaterialModalOpen}
        handleCloseMaterialDeleteModal={handleCloseMaterialDeleteModal}
        onDelete={handleDeleteMaterial}
        loading={deleteMaterialLoading}
        error={deleteMaterialError}
      />
    </>
  );
};

export default MaterialsForm;
